import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Form, Field, FieldProps, FormikProvider, useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { type AppState, type AppDispatch, Logger } from '@nextgen-web-framework/core';
import {
  useTranslation,
  Input,
  InputTypes,
  ButtonTypes,
  ButtonStyles,
  ButtonWidth,
  ButtonSize,
  AccountType,
  useBreakpoint,
  MLPVersion,
  Breakpoints,
  useSession,
  Theme,
  Notification,
  useFeatureVisibility,
  useNextgenRouter,
} from '@nextgen-web-framework/all';
import {
  ContactType,
  DynamicObjectInterface,
  OwnerType,
  DocumentImageDataWithTypeTypeEnum,
  updateRegistrationData,
  profileApi,
  AddressContactType,
  CreateChallengeRequest,
  CreateChallengeResponse,
  updateProfileInformationData,
  RegFlowConfig,
  RegistrationPatchPayload,
  RegistrationPatchRequest,
  FunctionType,
} from '@commerce-profile-webcomponents/sdk';

import ImageUpload from '../ImageUpload/ImageUpload';
import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import {
  ApiStatus,
  DATE_FORMAT,
  EDITOR_ASPECT_RATIO,
  ERROR_GENERIC_MESSAGE,
  ErrorConstants,
  SPONSOR_PREDEFINED_FLOW,
  TextConstants,
} from '../../utils/Constants';
import { APICallType, IFieldVisibility } from '../Registration/types/propTypes';
import { useCountryMasterData, useRegistrationFieldsMasterData } from '../../utils/masterdata';
import { isEmail } from '../../utils/helper';
import {
  addressInputCallbackForRegistration,
  convertDateObject,
  getFieldVisibility,
  getFlowValidation,
  getJWTToken,
  getRegistrationValidationData,
  markAllFieldTouched,
  registrationValidationSchemafn,
  sanitizeInput,
  sponsorFailedHandler,
  storeRegistrationInfoToSession,
} from '../../utils/registrationUtils';
import { RegistrationValidations } from '../RegistrationIdVerificationStep/types/propTypes';
import ApiErrorModal from '../ApiErrorModal/ApiErrorModal';
import { ApiErrorModalProps } from '../ApiErrorModal/types/propTypes';
import DesktopLayout from '../Common/DesktopLayout/DesktopLayout';
import RegistrationSponsor from '../RegistrationSponsor/RegistrationSponsor';
import DropDownwithMasterData from '../DropDownwithMasterData/DropDownwithMasterData';
import AddressInput from '../AddressInput/AddressInput';
import RegistrationUsername from '../RegistrationUsername/RegistrationUsername';
import TermsAndConditionCheckBox from '../Common/TermsAndConditionCheckBox/TermsAndConditionCheckBox';
import PersonalIdAndTypeInput from '../PersonalIdAndTypeInput/PersonalIdAndTypeInput';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import RegistrationOTPWrapper from '../RegistrationOTPWrapper/RegistrationOTPWrapper';
import { RegisterationSponsorRef } from '../RegistrationSponsor/types/propTypes';
import ButtonWithLoading from '../Common/ButtonWithLoading/ButtonWithLoading';
import RegistrationBirthDate from '../RegistrationBirthDate/RegistrationBirthDate';
import ChangeUsernameModal from '../ChangeUsernameModal/ChangeUsernameModal';
import { AddressInputHandlerType } from '../AddressInput/types/propTypes';
import { FloatErrorMessage } from '../Common/FloatErrorMessage/FloatErrorMessage';
import CoAppCommonScreen from '../CoApplicant/CoAppCommonScreen/CoAppCommonScreen';
import { CoAppScreenEnum } from '../CoApplicant/types/propTypes';
import { RegisterationUserNameRef } from '../RegistrationUsername/types/propTypes';
import { RegistrationUserDetailsStep } from './types/propTypes';
import styles from './RegistrationUserDetailsStep.module.scss';

const RegistrationUserDetailsStep = ({ nextHandler, hideAddressFields = false }: RegistrationUserDetailsStep) => {
  const logger = Logger.getInstance();
  const t = useTranslation();
  const globalErrorHandler = useGlobalErrorHandler();
  const dispatch = useDispatch<AppDispatch>();
  const viewPortType = useBreakpoint();
  const registrationFieldsMasterData = useRegistrationFieldsMasterData();
  const { currentCountry } = useCountryMasterData();
  const coAppRegFeatureFlag = useFeatureVisibility('profile.registration.coapplicant');
  const isMobile = [Breakpoints.XS, Breakpoints.SM].includes(viewPortType as Breakpoints);
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const accountTypeImagesCmsData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const personalDetailsImage = accountTypeImagesCmsData?.[selectedAccountType]?.personalDetailsImage;
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const {
    OtpData,
    perosonalDetails,
    coAppPersonalDetails,
    userData,
    flowName,
    currentIndex,
    selectSponsor,
    isRefreshRestoredData,
    isCoAppPageVisited,
    phonePrimaryContactSession,
    documentDataList,
    isChangeUsernameModalOpen,
    doNotKnowSponsor,
  } = registrationFlowData;
  const { sponsor, internationalSponsor, internationalSponsorCountryCode } = selectSponsor;
  const [isOtpDrawerOpen, setIsOtpDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [challengeId, setChallengeId] = useState<string>('');
  const { patchVersion, registrationId } = userData;
  const [showImageUploadNotification, setShowImageUploadNotification] = useState(false);
  const [modalErrorData, setModalErrorData] = useState<ApiErrorModalProps>();
  const registrationCmsData = useSelector((state: AppState) => state.commerceProfile.registrationCmsData);
  const termsNConditionsCmsData = useSelector((state: AppState) => state.commerceProfile.termsNConditionsCMS);
  const termsAndConditions = termsNConditionsCmsData?.[selectedAccountType]?.termsAndConditions;
  const { cidLoading, sampleCid, sampleCidBack } = registrationCmsData;
  const [isPhonePrimaryContact, setIsPhonePrimaryContact] = useState<boolean>();
  const [sponsorFailed, setSponsorFailed] = useState<boolean>(false);
  const [address, setAddress] = useState<AddressContactType>(perosonalDetails?.address);
  const defaultCountryDialCode = `+${currentCountry?.phoneCode}`;
  const [isSubmitClicked, setIsSubmitClicked] = useState<boolean>(false);
  const { isAnonymous } = useSession();
  const isAboUplineFlow = flowName === RegFlowConfig.AboLedFlow && !isAnonymous();
  const addressRef = useRef<AddressInputHandlerType>(null);
  const notificationDiv = useRef<HTMLDivElement>(null);
  const registerationSponsorRef = useRef<RegisterationSponsorRef>(null);
  const registerationUserNameRef = useRef<RegisterationUserNameRef>(null);
  const router = useNextgenRouter();

  const validationfields = useMemo(
    () =>
      getRegistrationValidationData({
        config: registrationFieldsMasterData as RegistrationValidations[],
        fieldNames: getFlowValidation(flowName),
      }),
    [],
  );
  const validationSchema = () => registrationValidationSchemafn(validationfields, selectedAccountType);

  const fieldVisibilityDictionary: IFieldVisibility = useMemo(() => {
    let data: IFieldVisibility = {};
    if (registrationFieldsMasterData?.length) {
      data = getFieldVisibility(registrationFieldsMasterData, 'pk', selectedAccountType, flowName);
    }
    return data;
  }, [selectedAccountType]);

  const initiateOtpHandler = async () => {
    setIsLoading(true);
    const configFieldName = isEmail(values?.userName ?? '') ? 'email' : 'phone';
    let updatedData = {
      ...registrationFlowData,
      OtpData: {
        ...OtpData,
        configFieldName,
      },
      userData: {
        ...userData,
      },
    };

    if (flowName !== RegFlowConfig.AboLedFlow) {
      const token = await getJWTToken(values?.userName ?? '', values.createPassword);
      updatedData = {
        ...updatedData,
        initialRegistrationForm: {
          ...registrationFlowData.initialRegistrationForm,
          inputForm: formik.values,
        },
        userData: {
          ...userData,
          jwtToken: token,
        },
        perosonalDetails: {
          ...perosonalDetails,
          isFieldDisabled: true,
        },
      };
    }

    // call challenge api
    const payload: CreateChallengeRequest = {
      [configFieldName]: values?.userName,
    };

    try {
      if (registrationId) {
        const challengeRes = await globalErrorHandler<CreateChallengeResponse>(() =>
          profileApi.postRegistrationChallenge({
            payload,
            registrationId,
          }),
        );

        setChallengeId(challengeRes?.challengeId ?? '');
        if (challengeRes?.version) {
          updatedData.userData.patchVersion = challengeRes.version;
        }
      }
      dispatch(updateRegistrationData(updatedData));
      setIsOtpDrawerOpen(true);
    } catch {
      setModalErrorData({
        headline: ERROR_GENERIC_MESSAGE.headline,
        body: ERROR_GENERIC_MESSAGE.headline,
      });
    }
    setIsLoading(false);
  };

  const onFormSubmitHandler = (addBusinessPartnerFlag = false) => {
    setIsSubmitClicked(true);
    formik.setTouched(markAllFieldTouched(values), true);
    addressRef?.current?.validate();
    registerationSponsorRef?.current?.validateSponsor();
    registerationUserNameRef?.current?.validateUserNamePassword();
    logger.log(formik.isValid, 'formik.isValid', formik.errors, formik?.status?.enableField, formik.values);
    const isFormValid = formik.isValid && Object.keys(formik.errors).length == 0 && formik?.status?.enableField;

    if (isFormValid) {
      logger.log('formik is valid', isFormValid);
      if (isAboUplineFlow || addBusinessPartnerFlag) {
        submitRegistrationdata(APICallType.PATCH, {}, addBusinessPartnerFlag);
      } else if (userNameChanged()) {
        initiateOtpHandler();
      } else if (flowName === RegFlowConfig.CoApplicantFlow) {
        createIdentityAndSubmit();
      } else if (OtpData?.isOTPVerified) {
        submitRegistrationdata(APICallType.PATCH);
      } else {
        initiateOtpHandler();
      }
    } else {
      logger.log('formik is invalid');
      notificationDiv?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  };

  const formik = useFormik({
    initialValues: perosonalDetails,
    onSubmit: () => {
      logger.log('submit');
    },
    validationSchema: validationSchema,
    validateOnBlur: true,
  });

  const isPersonalDetailsFormValid =
    formik.isValid && Object.keys(formik.errors).length == 0 && formik?.status?.enableField;

  const { values, handleBlur, setFieldValue, setStatus, resetForm } = formik;

  const userNameChanged = () =>
    isPhonePrimaryContact
      ? `${defaultCountryDialCode}${perosonalDetails?.userName}` !== values.userName
      : perosonalDetails?.userName !== values.userName;

  const createIdentityAndSubmit = async () => {
    const userNamePayload = isPhonePrimaryContact ? values.phone : values.email;
    const identityJWT = await getJWTToken(userNamePayload ?? '', values.createPassword);
    submitRegistrationdata(undefined, { identityJWT });
  };

  const submitRegistrationdata = async (
    apiCallType?: APICallType,
    paramPayload = {},
    addBusinessPartnerFlag = false,
  ) => {
    logger.log('Submit registration data to parent', values);
    setIsLoading(true);
    const payload: RegistrationPatchRequest = {
      ...paramPayload,
      givenName: values.firstName,
      familyName: values.lastName,
      version: patchVersion,
      phone: isPhonePrimaryContact ? values.userName : values.phone,
      email: values.email,
      doNotKnowSponsor,
      ...(challengeId && { otpRequestId: challengeId }),
      ...(values.taxRegime && { taxRegime: values.taxRegime }),
      ...(values.gender && { gender: values.gender }),
      ...(userData?.jwtToken && { identityJWT: userData.jwtToken }),
      ...(sponsor?.accountNumber && { sponsor: sponsor.accountNumber }),
      ...(address?.addrLineOne && fieldVisibilityDictionary['billingAddress'] && { billingAddress: address }),
    };

    if (values.birthDate) {
      payload.birthDate = convertDateObject(values.birthDate).format(DATE_FORMAT['YYYY-MM-DD']) as unknown as Date;
    }

    if (values.citizenshipCountry) {
      payload.citizenshipCountry = values.citizenshipCountry;
      //current logic without citizenship selection on UI.
      if (values.citizenshipCountry == currentCountry?.pk) {
        payload.citizenship = 'CITIZEN';
      } else {
        payload.citizenship = 'FOREIGNER';
      }
    }
    if (internationalSponsor && Object.keys(internationalSponsor).length !== 0) {
      payload.internationalSponsor = {
        accountNumber: internationalSponsor.accountNumber,
        countryCode: internationalSponsorCountryCode,
      };
    }
    const callOnSuccess = flowName === RegFlowConfig.AboLedFlow ? false : true;
    dispatch(
      updateProfileInformationData({
        address,
      }),
    );
    const res = await Promise.resolve(
      nextHandler(
        payload as RegistrationPatchPayload,
        undefined,
        false,
        callOnSuccess,
        apiCallType,
        '',
        addBusinessPartnerFlag,
      ),
    );
    // write code for session storage & update redux & use isPhonePrimaryContact

    if (res?.responseBody) {
      const { code, status } = res.responseBody;
      errorHandler(code, status);
    } else {
      const userName = isPhonePrimaryContact ? values?.userName?.replace(defaultCountryDialCode, '') : values?.userName;
      dispatch(
        updateRegistrationData({
          perosonalDetails: {
            ...perosonalDetails,
            ...values,
            userName,
            address: { ...address },
            termsAndConditionCheckbox: true,
            ...(addBusinessPartnerFlag && { createPassword: values.createPassword }),
          },
          ...(!addBusinessPartnerFlag && {
            OtpData: {
              ...OtpData,
              challengeId,
              isOTPVerified: true,
            },
          }),
          isRefreshRestoredData: !addBusinessPartnerFlag || coAppPersonalDetails?.userName,
          phonePrimaryContactSession: isPhonePrimaryContact,
          isCoAppPageVisited: false,
        }),
      );
      if (res?.registrationId && challengeId) {
        storeRegistrationInfoToSession(registrationId, challengeId, res?.currentIndex);
      }
    }
    setIsLoading(false);
  };

  const cidExpiredHandler = () => {
    dispatch(
      updateRegistrationData({
        ...registrationFlowData,
        currentIndex: currentIndex - 1,
      }),
    );
  };

  const handleSponsorFailedAndUpdateState = () =>
    sponsorFailedHandler(flowName, () => {
      dispatch(
        updateRegistrationData({
          selectSponsor: {
            ...selectSponsor,
            sponsor: {},
            editSponsor: true,
          },
        }),
      );
      setSponsorFailed(true);
    });

  const errorHandler = useCallback((code: string, status: number) => {
    if (status === 400) {
      if (code.includes(ApiStatus.BAD_REQUEST)) {
        setModalErrorData({
          headline: ERROR_GENERIC_MESSAGE.headline,
          body: ERROR_GENERIC_MESSAGE.body,
        });
      } else {
        const errorCallback = () => {
          dispatch(
            updateRegistrationData({
              ...registrationFlowData,
              //clearUserIdImage: true,
            }),
          );
        };
        let errorModalCallback = errorCallback;
        let buttonLabel = undefined;
        if (code === ErrorConstants.SPONSOR_FAILED) {
          buttonLabel = `profile.ApiErrorModal.${code}.button`;
          errorModalCallback = handleSponsorFailedAndUpdateState;
        } else if (code === ErrorConstants.CID_EXPIRED_CODE) {
          buttonLabel = `profile.ApiErrorModal.${code}.button`;
          errorModalCallback = cidExpiredHandler;
        }
        setModalErrorData({
          headline: `profile.ApiErrorModal.${code}.headline`,
          body: `profile.ApiErrorModal.${code}.body`,
          primaryButtonCallback: errorModalCallback,
          primaryButtonLabel: buttonLabel ?? '',
        });
      }
    } else {
      setModalErrorData({
        headline: ERROR_GENERIC_MESSAGE.headline,
        body: ERROR_GENERIC_MESSAGE.body,
      });
    }
  }, []);

  const imageUploadHandler = useCallback(
    async (
      image: string,
      imgType: DocumentImageDataWithTypeTypeEnum,
      setShowLoading?: FunctionType,
      setUploadSuccess?: FunctionType,
    ) => {
      const imageBase64 = image.split(',');
      const personalIdList = [
        {
          recordType: 'CED',
          documentDataList: [
            {
              type: imgType,
              documentData: imageBase64[1],
            },
          ],
        },
      ];

      const payload = {
        personalIdList,
        version: patchVersion,
      };

      setShowLoading?.(true); // loading before API call

      const res = await nextHandler(payload, undefined, true, false, APICallType.PATCH);
      setShowLoading?.(false);
      //TODO Need to reconfirm if this is the right way to handle errors ideally it should be something like promise resolved to error.
      if (res?.responseBody && res?.responseBody.code) {
        const { code, status } = res.responseBody;
        await setFieldValue('personalIdDocument', {
          ...values.personalIdDocument,
          [imgType]: false,
        });
        errorHandler(code, status);
      } else if (res) {
        //image loading flag also needs to be updated on the basis of response for front and back image
        setShowImageUploadNotification(true);
        setUploadSuccess?.(true);
        await setFieldValue('personalIdDocument', {
          ...values.personalIdDocument,
          [imgType]: true,
        });
      }
    },
    [patchVersion, nextHandler, setFieldValue, values.personalIdDocument, errorHandler, setShowImageUploadNotification],
  );

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (showImageUploadNotification) {
      timeoutId = setTimeout(() => {
        setShowImageUploadNotification(false);
      }, 3000);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [showImageUploadNotification]);

  const removeImageHandler = useCallback(
    (imgType: string) => {
      setShowImageUploadNotification(false);
      setFieldValue('personalIdDocument', { ...values.personalIdDocument, [imgType]: false });
    },
    [registrationFlowData],
  );

  // Moving inside callback to remove ImageUpload Rerendering
  const removeFrontImageHandler = useCallback(
    () => removeImageHandler(DocumentImageDataWithTypeTypeEnum.Front),
    [removeImageHandler],
  );
  const removeBackImageHandler = useCallback(
    () => removeImageHandler(DocumentImageDataWithTypeTypeEnum.Back),
    [removeImageHandler],
  );
  const frontImageUploadCallback = useCallback(
    (image: string, setShowLoading: FunctionType | undefined, setUploadWasSuccess: FunctionType | undefined) =>
      imageUploadHandler(image, DocumentImageDataWithTypeTypeEnum.Front, setShowLoading, setUploadWasSuccess),
    [imageUploadHandler],
  );
  const backImageUploadCallback = useCallback(
    (image: string, setShowLoading: FunctionType | undefined, setUploadWasSuccess: FunctionType | undefined) =>
      imageUploadHandler(image, DocumentImageDataWithTypeTypeEnum.Back, setShowLoading, setUploadWasSuccess),
    [imageUploadHandler],
  );

  useEffect(() => {
    if (isRefreshRestoredData || isCoAppPageVisited) {
      setStatus({ enableField: true });
      setIsPhonePrimaryContact(phonePrimaryContactSession);
      setChallengeId(OtpData?.challengeId);
      addressRef?.current?.validate();
      registerationSponsorRef?.current?.validateSponsor();
      registerationUserNameRef?.current?.validateUserNamePassword?.();
    }
  }, [isRefreshRestoredData, isCoAppPageVisited]);

  useEffect(() => {
    if (fieldVisibilityDictionary?.['sponsor']) {
      setFieldValue('sponsor', sponsor?.accountNumber);
    }
  }, [sponsor?.accountNumber]);

  const resetPersonalForm = useCallback(() => {
    resetForm({
      values: {
        ...perosonalDetails,
        firstName: '',
        lastName: '',
        createPassword: '',
        password: '',
        birthDate: '',
        gender: '',
        taxRegime: '',
        termsAndConditionCheckbox: false,
        citizenshipCountry: '',
        sponsor: sponsor?.accountNumber ?? '',
        ...(flowName !== RegFlowConfig.CoApplicantFlow && { email: '', phone: '', userName: '', secondaryContact: '' }),
      },
    });
    dispatch(
      updateRegistrationData({
        isRefreshRestoredData: false,
      }),
    );
    addressRef?.current?.resetErrors();
    registerationSponsorRef?.current?.resetSponsorValidation();
    registerationUserNameRef?.current?.resetUserNamePassword?.();
    setIsSubmitClicked(false);
    logger.log(formik, 'formik');
  }, [resetForm, sponsor?.accountNumber]);

  const nextHandlerCallback = useCallback(
    async (payload: DynamicObjectInterface) => {
      const response = await nextHandler(payload, undefined, true, false, APICallType.PATCH);
      return response;
    },
    [nextHandler],
  );

  const updateChangeUsernameModal = () => {
    dispatch(
      updateRegistrationData({
        isChangeUsernameModalOpen: false,
      }),
    );
  };

  const handlerUsernamePrimaryButton = async () => {
    updateChangeUsernameModal();
    setIsOtpDrawerOpen(false);
    await setFieldValue('userName', '');
    dispatch(
      updateRegistrationData({
        perosonalDetails: {
          ...perosonalDetails,
          userName: '',
        },
      }),
    );
    if (isPhonePrimaryContact) {
      await setFieldValue('phone', '');
    } else {
      await setFieldValue('email', '');
    }
    formik.setFieldTouched('userName', true);
  };

  const handleChangeUsernameSecondaryButton = () => {
    updateChangeUsernameModal();
    setIsOtpDrawerOpen(true);
  };

  const addBusinessPartner = () => onFormSubmitHandler(true);

  if (userData?.coAppRegisterSuccess) {
    return <CoAppCommonScreen screenType={CoAppScreenEnum.COMPLETED} buttonHandler={() => router?.push('/')} />;
  }

  return (
    <DesktopLayout desktopImage={personalDetailsImage} imageClassName={styles['desktop-image']}>
      <div
        className={styles['registration-personal-details-wrapper']}
        data-testid="registration-personal-details-wrapper"
      >
        <div
          className={styles['registration-personal-details-conatiner']}
          data-testid="registration-personal-details-conatiner"
        >
          <FormikProvider value={formik}>
            <Form className={styles['registration-form']}>
              <div
                className={styles['registration-personal-details-heading']}
                data-testid="registration-personal-details-heading"
              >
                {t('profile.RegistrationPersonalDetailsStep.heading')}
              </div>

              <div className={styles['input-form']} data-testid="input-form" ref={notificationDiv}>
                {isSubmitClicked && !isPersonalDetailsFormValid && (
                  <div className={styles['notification-bar']}>
                    <Notification
                      description=""
                      message={t('profile.RegistrationPersonalDetailsStep.errorHeading')}
                      hasBoldText={false}
                      textLink={{
                        href: '',
                        text: '',
                      }}
                      theme={Theme.ERROR}
                    />
                  </div>
                )}

                <div
                  className={`${styles['input-form-wrapper']} ${hideAddressFields ? styles['co-app-ui'] : ''}`}
                  data-testid="input-form-wrapper"
                >
                  <div className={styles['info-section']}>
                    <div className={styles['input-wrapper']}>
                      <PersonalIdAndTypeInput
                        nextHandlerCallback={nextHandlerCallback}
                        resetPersonalForm={resetPersonalForm}
                        isRefreshRestoredData={isRefreshRestoredData}
                      />
                    </div>

                    {isSubmitClicked && formik?.errors?.personalIdDocument && (
                      <div className={styles['notification-bar']}>
                        <Notification
                          description=""
                          message={t('profile.RegistrationPersonalDetailsStep.imageError')}
                          hasBoldText={false}
                          textLink={{
                            href: '',
                            text: '',
                          }}
                          theme={Theme.ERROR}
                        />
                      </div>
                    )}

                    <div
                      className={`${styles['input-fields']} 
                        ${!fieldVisibilityDictionary?.['personalIdDocument'] ? styles['input-hide'] : ''}`}
                    >
                      <span className={styles['image-message']}>
                        {t('profile.RegistrationUsersDetailsStep.aboIdNote')}
                      </span>
                      <ul className={styles['image-message']} style={{ listStyleType: 'decimal' }}>
                        <li>{t('profile.RegistrationUsersDetailsStep.aboIdFrontImageNote')}</li>
                        <li>{t('profile.RegistrationUsersDetailsStep.aboIdBackImageNote')}</li>
                      </ul>
                    </div>
                    <div
                      className={`${styles['image-section']} ${
                        !fieldVisibilityDictionary?.['personalIdDocument'] ? styles['input-hide'] : ''
                      }`}
                    >
                      <Field name="personalIdDocument" as="checkbox">
                        {() => (
                          <>
                            <ImageUpload
                              uploadLabel={t('profile.ImageUpload.uploadLabel.front')}
                              callback={frontImageUploadCallback}
                              wrapperClass={styles['upload-cid-wrapper']}
                              imageClass={styles['sample-image']}
                              sampleImage={sampleCid}
                              loadingImage={cidLoading}
                              canRemoveAfterSuccess={true}
                              removeIconHandler={removeFrontImageHandler}
                              testId="image0"
                              showImagePreview={false}
                              editorAspectRatio={EDITOR_ASPECT_RATIO.CID}
                              disabled={!formik.status?.enableField}
                              defaultImageBase64={
                                documentDataList?.find(
                                  (documentData: { type: DocumentImageDataWithTypeTypeEnum }) =>
                                    documentData.type === DocumentImageDataWithTypeTypeEnum.Front,
                                )?.['documentData']
                              }
                            />

                            <ImageUpload
                              uploadLabel={t('profile.ImageUpload.uploadLabel.back')}
                              callback={backImageUploadCallback}
                              wrapperClass={styles['upload-cid-wrapper']}
                              imageClass={styles['sample-image']}
                              sampleImage={sampleCidBack}
                              loadingImage={cidLoading}
                              canRemoveAfterSuccess={true}
                              removeIconHandler={removeBackImageHandler}
                              testId="image1"
                              showImagePreview={false}
                              editorAspectRatio={EDITOR_ASPECT_RATIO.CID}
                              disabled={!formik.status?.enableField}
                              defaultImageBase64={
                                documentDataList?.find(
                                  (documentData: { type: DocumentImageDataWithTypeTypeEnum }) =>
                                    documentData.type === DocumentImageDataWithTypeTypeEnum.Back,
                                )?.['documentData']
                              }
                            />
                          </>
                        )}
                      </Field>
                    </div>
                  </div>

                  <div className={styles['info-section']}>
                    <div className={styles['input-wrapper']} data-testid="name-wrapper">
                      <div className={styles['input-fields']} data-testid="input-fname-field">
                        <Field type="text" name="firstName">
                          {({ meta }: FieldProps) => (
                            <Input
                              id="firstName"
                              data-testid="firstName"
                              type={InputTypes.TEXT}
                              name="firstName"
                              value={values.firstName}
                              label={t('profile.RegistrationPersonalDetailsStep.firstName')}
                              onChange={(e) => {
                                const val = sanitizeInput(e.target.value);
                                setFieldValue('firstName', val);
                              }}
                              onBlur={handleBlur}
                              mlpVersion={MLPVersion.TWO}
                              status={inputErrorStatus(meta.touched, meta.error)}
                              message={inputErrorMessage(t, meta.touched, meta.error, {
                                max: TextConstants.NAME_MAX,
                              })}
                              disabled={!formik.status?.enableField}
                            />
                          )}
                        </Field>
                      </div>

                      <div className={styles['input-fields']}>
                        <Field type="text" name="lastName">
                          {({ meta }: FieldProps) => (
                            <Input
                              id="lastName"
                              data-testid="lastName"
                              type={InputTypes.TEXT}
                              name="lastName"
                              label={t('profile.RegistrationPersonalDetailsStep.lastName')}
                              onChange={(e) => {
                                const val = sanitizeInput(e.target.value);
                                setFieldValue('lastName', val);
                              }}
                              value={values.lastName}
                              onBlur={handleBlur}
                              mlpVersion={MLPVersion.TWO}
                              status={inputErrorStatus(meta.touched, meta.error)}
                              message={inputErrorMessage(t, meta.touched, meta.error, {
                                max: TextConstants.NAME_MAX,
                              })}
                              disabled={!formik.status?.enableField}
                            />
                          )}
                        </Field>
                      </div>
                    </div>

                    <div
                      className={`${styles['input-wrapper']} ${
                        !fieldVisibilityDictionary?.['gender'] && !fieldVisibilityDictionary['birthDate']
                          ? styles['input-hide']
                          : ''
                      }`}
                    >
                      <div className={`${styles['input-fields']} ${isMobile ? styles['remove-margin-bottom'] : ''}`}>
                        <RegistrationBirthDate
                          name="birthDate"
                          selectedAccountType={selectedAccountType}
                          isLunar={false}
                          mlpVersionProp={MLPVersion.TWO}
                        />
                      </div>

                      <div className={`${styles['input-fields']}`}>
                        <Field type="text" name="gender">
                          {() => (
                            <DropDownwithMasterData
                              fieldName="gender"
                              value={values.gender}
                              onSelect={(val) => setFieldValue('gender', val)}
                              disabled={!formik.status?.enableField}
                            />
                          )}
                        </Field>
                      </div>
                    </div>

                    <div className={styles['input-wrapper']}>
                      <div
                        className={`${styles['input-fields']} ${
                          !fieldVisibilityDictionary['taxRegime'] ? styles['input-hide'] : ''
                        }`}
                      >
                        <Field type="text" name="taxRegime">
                          {() => (
                            <DropDownwithMasterData
                              fieldName="taxRegime"
                              value={values.taxRegime}
                              onSelect={(key) => setFieldValue('taxRegime', key)}
                              disabled={!formik.status?.enableField}
                            />
                          )}
                        </Field>
                      </div>

                      <div
                        className={`${styles['input-fields']} ${
                          !fieldVisibilityDictionary['citizenshipCountry'] ? styles['input-hide'] : ''
                        }`}
                      >
                        <Field type="text" name="citizenshipCountry">
                          {() => (
                            <DropDownwithMasterData
                              fieldName="citizenshipCountry"
                              value={values.citizenshipCountry}
                              onSelect={(key) => setFieldValue('citizenshipCountry', key)}
                              disabled={!formik.status?.enableField}
                              translationPrefix="profile.RegistrationIdVerificationStep.country"
                              showSearch={true}
                            />
                          )}
                        </Field>
                      </div>
                    </div>
                  </div>
                  <RegistrationUsername
                    isPhonePrimaryContact={isPhonePrimaryContact}
                    setIsPhonePrimaryContact={setIsPhonePrimaryContact}
                    showPasswordField={flowName !== RegFlowConfig?.AboLedFlow ? true : false}
                    nextHandler={nextHandler}
                    showSecondaryContactField={true}
                    ref={registerationUserNameRef}
                    isRefreshRestoredData={isRefreshRestoredData || isCoAppPageVisited}
                  />
                </div>

                {/* ---------address-container-------- */}
                <div className={`${styles['address-container']}`}>
                  {!hideAddressFields && (
                    <>
                      <div
                        className={`${styles['address-input-field']} ${
                          !fieldVisibilityDictionary['billingAddress'] ? styles['input-hide'] : ''
                        }`}
                      >
                        <Field name="billingAddress">
                          {() => (
                            <AddressInput
                              title={t('profile.RegistrationPersonalDetailsStep.primaryAddress')}
                              hideConfirmButton={true}
                              callback={(contact: DynamicObjectInterface) => {
                                addressInputCallbackForRegistration(contact, () => {
                                  setAddress(contact);
                                  if (contact?.feValidation) {
                                    setFieldValue('billingAddress', true);
                                  } else {
                                    setFieldValue('billingAddress', false);
                                  }

                                  dispatch(
                                    updateRegistrationData({
                                      perosonalDetails: {
                                        ...perosonalDetails,
                                        isAddressSearchFocused: false,
                                      },
                                    }),
                                  );
                                });
                              }}
                              accountNumber={''}
                              contactType={ContactType.Mailing}
                              ownerType={OwnerType.Account}
                              useAvailableWidth={!isMobile}
                              disabled={!formik.status?.enableField}
                              address={address}
                              ref={addressRef}
                            />
                          )}
                        </Field>
                      </div>
                      {/* ---------sponsor-container-------- */}
                      <div
                        className={`${styles['info-section']} ${
                          !fieldVisibilityDictionary['sponsor'] ? styles['input-hide'] : ''
                        }`}
                      >
                        <div className={styles['info-sub-section']}>
                          <div className={styles['id-section-label']}>
                            {t('profile.RegistrationUsersDetailsStep.sponsorLabel')}:
                          </div>
                          <div className={styles['input-wrapper']}>
                            <div className={styles['input-fields']}>
                              <Field name="sponsor">
                                {() => (
                                  <RegistrationSponsor
                                    isFieldDisabled={!formik?.status?.enableField}
                                    showABOSelected={SPONSOR_PREDEFINED_FLOW.includes(flowName) ? true : false}
                                    isFocused={sponsorFailed}
                                    wrapperClass={styles['sponsor-details']}
                                    state={address?.state}
                                    city={address?.city}
                                    onLeadsSearchHandler={(value: boolean) => {
                                      logger.log('lead search', value);
                                      dispatch(updateRegistrationData({ doNotKnowSponsor: value }));
                                    }}
                                    ref={registerationSponsorRef}
                                  />
                                )}
                              </Field>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={`${styles['info-section']} ${styles['remove-border']}`}>
                        <div className={styles['info-sub-section']}>
                          <Field type="checkbox" name="termsAndConditionCheckbox">
                            {({ meta }: FieldProps) => (
                              <>
                                <TermsAndConditionCheckBox
                                  tncText={t('profile.RegistrationIdVerificationStep.checkboxLabel')}
                                  tncLinkText={t('profile.RegistrationIdVerificationStep.TNCcheckbox')}
                                  tncContent={termsAndConditions}
                                  disabled={!formik.status?.enableField}
                                  onCheckBoxClick={(val) => {
                                    setFieldValue('termsAndConditionCheckbox', !!val);
                                  }}
                                  checked={formik.values.termsAndConditionCheckbox}
                                />
                                {meta?.error && meta?.touched && (
                                  <FloatErrorMessage
                                    message={t('profile.RegistrationPersonalDetailsStep.tnc.required')}
                                  />
                                )}
                              </>
                            )}
                          </Field>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* ---------verification-btn-------- */}
                <div className={styles['verification-btn']}>
                  <ButtonWithLoading
                    title={
                      isAboUplineFlow
                        ? t('profile.common.nextButton')
                        : userNameChanged()
                          ? t('profile.RegistrationPersonalDetailsStep.verificationButton')
                          : OtpData?.isOTPVerified
                            ? t('profile.common.nextButton')
                            : t('profile.RegistrationPersonalDetailsStep.verificationButton')
                    }
                    type={ButtonTypes.BUTTON}
                    data-testid="verification-btn-testid"
                    buttonStyle={ButtonStyles.SOLID}
                    size={ButtonSize.MEDIUM}
                    disabled={!formik.status?.enableField || isLoading}
                    buttonWidth={ButtonWidth.FULL_WIDTH}
                    onClick={() => onFormSubmitHandler()}
                    isLoading={isLoading}
                  />
                  {selectedAccountType === AccountType.BusinessOwner && coAppRegFeatureFlag && (
                    <button
                      disabled={!formik.status?.enableField || isLoading}
                      className={styles['add-coapp-btn']}
                      onClick={addBusinessPartner}
                      data-testid={'previous-step-button'}
                    >
                      {coAppPersonalDetails?.userName
                        ? t('profile.RegistrationPersonalDetailsStep.editCoAppButton')
                        : t('profile.RegistrationPersonalDetailsStep.addCoAppButton')}
                    </button>
                  )}
                </div>
                {isOtpDrawerOpen && (
                  <RegistrationOTPWrapper
                    nextHandler={() => {
                      submitRegistrationdata();
                    }}
                    username={values.userName ?? ''}
                    challengeId={challengeId}
                    //TODO change signature to accept submitRegistrationdata
                    onCloseDrawer={() => {
                      // disableFields(false);
                      setIsOtpDrawerOpen(false);
                    }}
                    onUsernameChangeHandler={() => setIsOtpDrawerOpen(false)}
                  />
                )}
              </div>
            </Form>
          </FormikProvider>
        </div>

        {(modalErrorData?.headline || modalErrorData?.body) && <ApiErrorModal {...modalErrorData} />}
        {isChangeUsernameModalOpen && (
          <ChangeUsernameModal
            isUsernameEmail={!isPhonePrimaryContact}
            onSecondaryButtonClick={handleChangeUsernameSecondaryButton}
            onPrimaryButtonClick={handlerUsernamePrimaryButton}
          />
        )}
      </div>
    </DesktopLayout>
  );
};
export default RegistrationUserDetailsStep;
